import React, { useMemo, useState, useEffect } from 'react';
import { runHMMForAllTopics, STATE_META } from '../utils/hmm';
import { API_BASE_URL, apiFetch, isDemoMode } from '../config';

/**
 * Builds a state-aware quiz message that includes:
 *  1. Natural language request framed to match the student's HMM state
 *  2. An explicit HMM context block so the AI knows the state + confidence
 *
 * The curriculum lockdown in the system prompt already handles topic gating
 * (no future-slide content). Adaptive hints are intentionally skipped because
 * the HMM state is richer than raw accuracy.
 */
function buildQuizMessage(subject, stateIndex, confidence, formalCount, informalCount) {
  const pct = Math.round(confidence * 100);
  const evidenceParts = [`${formalCount} quiz answer${formalCount !== 1 ? 's' : ''}`];
  if (informalCount > 0) evidenceParts.push(`${informalCount} chat answer${informalCount !== 1 ? 's' : ''}`);
  const evidence = evidenceParts.join(' + ');

  const stateNames = ['Not Learned', 'Learning', 'Mastered', 'Forgetting'];
  const stateName  = stateNames[stateIndex] || 'Unknown';

  let naturalLanguage;
  switch (stateIndex) {
    case 0: // Not Learned
      naturalLanguage = `I haven't fully learned "${subject}" yet. Can you give me a brief explanation and then ask me a beginner-level multiple choice question about it?`;
      break;
    case 1: // Learning
      naturalLanguage = `I'm still learning "${subject}". Can you give me a multiple choice practice question to help me build understanding?`;
      break;
    case 2: // Mastered
      naturalLanguage = `I feel confident about "${subject}". Can you give me a challenging multiple choice question to test my mastery?`;
      break;
    case 3: // Forgetting
      naturalLanguage = `I think I'm starting to forget "${subject}". Can you give me a review multiple choice question to help me remember?`;
      break;
    default:
      naturalLanguage = `Can you give me a multiple choice question about "${subject}"?`;
  }

  const hmmContext =
    `\n\n[HMM Knowledge State: ${stateName} | Confidence: ${pct}% | Evidence: ${evidence}]` +
    `\nPlease tailor the question difficulty and framing strictly to this knowledge state. ` +
    `Do NOT use adaptive hint levels for this request — use the HMM state above instead.`;

  return naturalLanguage + hmmContext;
}

/**
 * KnowledgeStatesPanel
 *
 * Displays per-topic knowledge states estimated by a 4-state Hidden Markov Model.
 * Each topic's binary answer history is decoded using the Viterbi algorithm with
 * fixed BKT-derived parameters. See HMM_DOCUMENTATION.md for full details.
 *
 * Observation sources:
 *   1. masteryHistory prop  — formal quiz answers from student_mastery_history (weight 1.0)
 *   2. question_answered    — informal chat Q&A fetched on mount (weight 0.5, downsampled)
 *
 * Props:
 *   masteryHistory  {Array}    Flat array of { subject, correct, wrong, recorded_at }
 *   username        {string}
 *   onClose         {Function}
 *   onQuizTopic     {Function} (message: string, action: string) => void  — optional
 */
export default function KnowledgeStatesPanel({ masteryHistory, username, onClose, onQuizTopic }) {
  const MIN_OBS = 5;

  const [qaHistory, setQaHistory]   = useState([]);
  const [qaLoading, setQaLoading]   = useState(true);

  // Fetch informal Q&A history on mount
  useEffect(() => {
    if (!username || isDemoMode()) { setQaLoading(false); return; }
    apiFetch(`${API_BASE_URL}/api/studentQuestionAnsweredAll?username=${encodeURIComponent(username)}&limit=1000`)
      .then(res => res.ok ? res.json() : { historyquestionanswered: [] })
      .then(data => {
        setQaHistory(data.historyquestionanswered || []);
        setQaLoading(false);
      })
      .catch(() => setQaLoading(false));
  }, [username]);

  const topicResults = useMemo(
    () => runHMMForAllTopics(masteryHistory, MIN_OBS, qaHistory),
    [masteryHistory, qaHistory]
  );

  const sufficient   = topicResults.filter(t => !t.insufficient);
  const insufficient = topicResults.filter(t =>  t.insufficient);

  // Total informal observations used (after downsampling)
  const totalInformalUsed = topicResults.reduce((s, t) => s + (t.informalCount || 0), 0);

  // State summary counts
  const stateCounts = [0, 1, 2, 3].map(idx =>
    sufficient.filter(t => t.result && t.result.stateIndex === idx).length
  );

  // Demo mode: all hooks have run, now safe to return early
  if (isDemoMode()) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.card} onClick={e => e.stopPropagation()}>
          <div style={styles.header}>
            <div>
              <h2 style={styles.title}>🔮 Knowledge States</h2>
            </div>
            <button style={styles.closeBtn} onClick={onClose}>✕</button>
          </div>
          <div style={{ padding: '32px 24px', color: '#fffacd', whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '14px' }}>
            {`🔒 Knowledge States — Locked in Demo Mode

With a real account this panel uses a Hidden Markov Model (HMM) to estimate
your knowledge state for every Python topic you've studied.

Each topic gets assigned one of four states:

  🌱  Not Learned   — you haven't encountered this topic in quizzes yet
  📘  Learning      — you're getting it right sometimes but not consistently
  ✅  Mastered      — you answer correctly and consistently
  ⚠️  Forgetting    — you used to get it right but recent answers are slipping

Example view after a few weeks of quizzing:

  Topic              State         Confidence
  ──────────────────────────────────────────
  Variables          ✅ Mastered       91%
  For Loops          ⚠️  Forgetting     74%
  Functions          📘 Learning       63%
  File I/O           🌱 Not Learned    —

How it helps:
  • Click any topic → the tutor sends you a question pitched exactly at
    your current state (beginner, review, challenge, etc.)
  • The model combines formal quiz answers AND casual chat Q&A to build
    a richer picture than accuracy alone

✨ Sign up to unlock your Knowledge States!`}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.card} onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>🔮 Knowledge States</h2>

          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* ── Legend / summary bar ── */}
        <div style={styles.legendRow}>
          {STATE_META.map((m, i) => (
            <div key={i} style={{ ...styles.legendBadge, background: m.bg, borderColor: m.color }}>
              <span style={{ fontSize: 16 }}>{m.emoji}</span>
              <span style={{ color: m.color, fontWeight: 600 }}>{m.label}</span>
              <span style={{ ...styles.legendCount, background: m.color }}>{stateCounts[i]}</span>
            </div>
          ))}
        </div>

        {/* ── No data case ── */}
        {topicResults.length === 0 && (
          <div style={styles.emptyMsg}>
            No mastery history found for <strong>{username}</strong>.<br />
            Answer some practice questions to see your knowledge states.
          </div>
        )}

        {/* ── Topic grid ── */}
        <div style={styles.grid}>
          {sufficient.map(({ subject, result, observationCount, formalCount, informalCount }) => {
            const meta      = STATE_META[result.stateIndex];
            const pct       = Math.round(result.confidence * 100);
            const clickable = !!onQuizTopic;
            const handleClick = () => {
              if (!onQuizTopic) return;
              const msg = buildQuizMessage(subject, result.stateIndex, result.confidence, formalCount, informalCount);
              onClose();
              onQuizTopic(msg, 'Quiz Me (Current)');
            };
            return (
              <div
                key={subject}
                onClick={clickable ? handleClick : undefined}
                title={clickable ? `Click to practice "${subject}"` : undefined}
                style={{
                  ...styles.topicCard,
                  borderColor: meta.color,
                  background: meta.bg,
                  ...(clickable ? styles.topicCardClickable : {}),
                }}
              >
                <div style={styles.topicHeader}>
                  <span style={styles.topicEmoji}>{meta.emoji}</span>
                  <span style={styles.topicName}>{subject}</span>
                  {clickable && <span style={styles.practiceHint}>Practice →</span>}
                </div>
                <div style={{ ...styles.stateLabel, color: meta.color }}>{meta.label}</div>

                {/* Confidence bar */}
                <div style={styles.barTrack}>
                  <div style={{ ...styles.barFill, width: `${pct}%`, background: meta.color }} />
                </div>
                <div style={styles.barLabel}>
                  Confidence: <strong>{pct}%</strong>
                  <span style={styles.obsCount}>
                    {formalCount}quiz{informalCount > 0 ? ` +${informalCount}chat` : ''}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Insufficient data topics */}
          {insufficient.map(({ subject, observationCount }) => (
            <div key={subject} style={styles.insufficientCard}>
              <div style={styles.topicHeader}>
                <span style={styles.topicEmoji}>⏳</span>
                <span style={styles.topicName}>{subject}</span>
              </div>
              <div style={styles.insufficientLabel}>Insufficient data</div>
              <div style={styles.insufficientSub}>
                {observationCount} / {MIN_OBS} answers needed
              </div>
            </div>
          ))}
        </div>

        {/* ── Footer note ── */}
        <div style={styles.footer}>
          <span style={{ opacity: 0.5 }}>🔬</span>&nbsp;
          States are inferred using a Bayesian Knowledge Tracing–derived Hidden Markov Model.
          Estimates improve as more answers are recorded.
          &nbsp;<span style={{ opacity: 0.5 }}>Min. {MIN_OBS} answers per topic required.</span>
          {onQuizTopic && (
            <span style={{ display: 'block', marginTop: 6, color: '#4ade80' }}>
              💬 Click any topic card to start a practice question tailored to your knowledge state.
            </span>
          )}
        </div>

      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.85)',
    zIndex: 9999,
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    overflowY: 'auto',
    padding: '40px 16px',
  },
  card: {
    background: '#1f2937',
    border: '2px solid rgba(253,224,71,0.5)',
    borderRadius: 16,
    width: '100%',
    maxWidth: 1100,
    padding: '28px 32px',
    display: 'flex', flexDirection: 'column', gap: 20,
    boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
  },
  title: {
    margin: 0, fontSize: 22, fontWeight: 700, color: '#fef9c3',
  },
  subtitle: {
    margin: '4px 0 0', fontSize: 13, color: '#fef9c3',
  },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#fef9c3', fontSize: 20, padding: 4, lineHeight: 1,
  },
  legendRow: {
    display: 'flex', gap: 10, flexWrap: 'wrap',
    marginBottom: 28,
  },
  legendBadge: {
    display: 'flex', alignItems: 'center', gap: 6,
    border: '1px solid', borderRadius: 20,
    padding: '4px 12px', fontSize: 13,
  },
  legendCount: {
    color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 12, fontWeight: 700,
  },
  emptyMsg: {
    textAlign: 'center', color: '#fef9c3', padding: '32px 0', fontSize: 14,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 14,
  },
  topicCard: {
    border: '1.5px solid', borderRadius: 10, padding: '14px 16px',
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  topicCardClickable: {
    cursor: 'pointer',
    transition: 'opacity 0.15s ease',
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
  },
  practiceHint: {
    fontSize: 11, color: '#6366f1', fontWeight: 600, opacity: 0.8, whiteSpace: 'nowrap',
  },
  topicHeader: {
    display: 'flex', alignItems: 'center', gap: 8,
  },
  topicEmoji: { fontSize: 18 },
  topicName: {
    fontSize: 13, fontWeight: 600, color: '#fefce8',
    flex: 1, lineHeight: 1.3,
  },
  stateLabel: {
    fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  barTrack: {
    height: 6, background: 'rgba(255,255,255,0.1)', borderRadius: 3, overflow: 'hidden',
  },
  barFill: {
    height: '100%', borderRadius: 3, transition: 'width 0.5s ease',
  },
  barLabel: {
    fontSize: 11, color: '#fef9c3', display: 'flex', justifyContent: 'space-between',
  },
  obsCount: {
    fontSize: 11, color: '#fef9c3',
  },
  insufficientCard: {
    border: '1.5px solid rgba(107,114,128,0.4)', borderRadius: 10, padding: '14px 16px',
    background: 'rgba(107,114,128,0.07)',
    display: 'flex', flexDirection: 'column', gap: 6,
  },
  insufficientLabel: {
    fontSize: 12, fontWeight: 600, color: '#fef9c3', textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  insufficientSub: {
    fontSize: 11, color: '#fef9c3',
  },
  footer: {
    fontSize: 12, color: '#fef9c3', borderTop: '1px solid rgba(255,255,255,0.06)',
    paddingTop: 14, lineHeight: 1.6,
  },
};
